import React, { useEffect, useRef, useState } from "react";
import { Image as ImageIcon } from "lucide-react";
import { api } from "../lib/api";

export default function GalleryImage({ src, alt = "Gallery image", className = "", imageTestId, fallbackTestId = "gallery-image-fallback", onError }) {
  const [resolvedSource, setResolvedSource] = useState("");
  const [loading, setLoading] = useState(Boolean(src));
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    let active = true;
    let objectUrl = null;
    setResolvedSource("");
    setLoading(Boolean(src));
    if (!src) return undefined;

    if (!src.startsWith("/api/")) {
      setResolvedSource(src);
      setLoading(false);
      return undefined;
    }

    api.get(src, { responseType: "blob" })
      .then(response => {
        const contentType = response.data?.type || response.headers?.["content-type"] || "";
        if (!contentType.startsWith("image/")) throw new Error("Protected poster response was not an image");
        objectUrl = URL.createObjectURL(response.data);
        if (active) setResolvedSource(objectUrl);
      })
      .catch(() => {
        if (active) onErrorRef.current?.();
      })
      .finally(() => { if (active) setLoading(false); });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  if (!resolvedSource) {
    return (
      <div className={`${className} flex flex-col items-center justify-center bg-zinc-900 text-zinc-500`}
        role="img" aria-label={loading ? `Loading ${alt}` : `${alt} unavailable`}
        data-testid={fallbackTestId}>
        <ImageIcon size={24} />
        <span className="mt-1 text-[9px] uppercase tracking-wide">GioPilos</span>
      </div>
    );
  }

  return <img src={resolvedSource} alt={alt} data-testid={imageTestId} className={className} loading="lazy" onError={() => {
    setResolvedSource("");
    onErrorRef.current?.();
  }} />;
}
